import { Loader2, Search, X } from "lucide-react";
import { forwardRef, type InputHTMLAttributes } from "react";

export type SearchInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  onClear?: () => void;
  loading?: boolean;
};

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ onClear, loading = false, className = "", value, disabled, ...props }, ref) => {
    const hasValue = value !== undefined && value !== "";

    return (
      <div className={`relative ${className}`}>
        {/* Search icon */}
        <Search
          className={`
            absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none
            ${disabled ? "text-content-faint" : "text-content-muted"}
          `}
        />

        <input
          ref={ref}
          type="search"
          value={value}
          disabled={disabled}
          className={`
            w-full h-10 pl-10 pr-10 rounded-lg bg-surface-800 border text-sm text-white
            placeholder-content-muted transition-colors duration-200
            focus:outline-none focus:border-brand-500
            disabled:opacity-50 disabled:cursor-not-allowed
            border-surface-700 hover:border-surface-600

            [&::-webkit-search-cancel-button]:hidden
            [&::-webkit-search-decoration]:hidden
          `}
          {...props}
        />

        {/* Right side: loading spinner or clear button */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {loading ? (
            <Loader2 className="w-5 h-5 text-content-muted animate-spin" />
          ) : hasValue && onClear ? (
            <button
              type="button"
              onClick={onClear}
              disabled={disabled}
              className={`
                p-0.5 rounded-full text-content-muted hover:text-white hover:bg-surface-700
                transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            >
              <X className="w-4 h-4" />
            </button>
          ) : null}
        </div>
      </div>
    );
  },
);

SearchInput.displayName = "SearchInput";
